/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Button, ITelemetryContent, getPayloadObject, getTelemetryAttributes } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

const componentClassName = 'msc-invoices-organization-wide-selector';
const buttonClassName = `${componentClassName}__button`;
const buttonActiveClassName = `${componentClassName}__active-button`;
const buttonInactiveClassName = `${componentClassName}__inactive-button`;

const currentUserTelemetryLabel = 'Current user invoices';
const organizationWideTelemetryLabel = 'Organization-wide invoices';
const showAllTelemetryLabel = 'Show all invoices';

/**
 * Resources for the invoices organization-wide selector.
 */
export interface IInvoicesOrganizationWideSelectorResources {
    currentUserOptionLabel: string;
    currentUserOptionAriaLabel?: string;
    organizationWideOptionLabel: string;
    organizationWideOptionAriaLabel?: string;
    showAllOptionLabel?: string;
    showAllOptionAriaLabel?: string;
}

/**
 * Available invoice scopes.
 */
export enum InvoicesOrganizationWideSelectorState {
    CurrentUser,
    OrganizationWide,
    ShowAll
}

/**
 * Props for the invoices organization-wide selector.
 */
export interface IInvoicesOrganizationWideSelectorProps extends IComponentProps<{}> {
    className?: string;
    resources: IInvoicesOrganizationWideSelectorResources;
    currentSelectorState: InvoicesOrganizationWideSelectorState;
    telemetryContent?: ITelemetryContent;
    onChange(state: InvoicesOrganizationWideSelectorState): void;
}

/**
 * Selector for switching between current-user, organization-wide, and all invoices.
 * @param props - Component properties.
 * @returns The selector.
 */
const InvoicesOrganizationWideSelectorFunctionalComponent: React.FC<IInvoicesOrganizationWideSelectorProps> = props => {
    const {
        currentSelectorState,
        telemetryContent,
        resources: {
            currentUserOptionLabel,
            organizationWideOptionLabel,
            currentUserOptionAriaLabel,
            organizationWideOptionAriaLabel,
            showAllOptionLabel,
            showAllOptionAriaLabel
        }
    } = props;
    const [selectorState, setSelectorState] = React.useState(currentSelectorState);

    React.useEffect(() => {
        setSelectorState(currentSelectorState);
    }, [currentSelectorState]);

    const onClickHandler = (newSelectorState: InvoicesOrganizationWideSelectorState) => {
        setSelectorState(newSelectorState);
        props.onChange(newSelectorState);
    };

    const currentUserAttributes = telemetryContent
        ? getTelemetryAttributes(
              telemetryContent,
              getPayloadObject('click', telemetryContent, currentUserOptionLabel || currentUserTelemetryLabel)
          )
        : {};
    const organizationWideAttributes = telemetryContent
        ? getTelemetryAttributes(
              telemetryContent,
              getPayloadObject('click', telemetryContent, organizationWideOptionLabel || organizationWideTelemetryLabel)
          )
        : {};
    const showAllAttributes = telemetryContent
        ? getTelemetryAttributes(telemetryContent, getPayloadObject('click', telemetryContent, showAllOptionLabel || showAllTelemetryLabel))
        : {};

    return (
        <div className={classnames(props.className, componentClassName)}>
            <Button
                id='current-user-button'
                className={classnames(
                    buttonClassName,
                    selectorState === InvoicesOrganizationWideSelectorState.CurrentUser ? buttonActiveClassName : buttonInactiveClassName
                )}
                aria-label={currentUserOptionAriaLabel || currentUserOptionLabel}
                title={currentUserOptionAriaLabel || currentUserOptionLabel}
                onClick={() => onClickHandler(InvoicesOrganizationWideSelectorState.CurrentUser)}
                {...currentUserAttributes}
            >
                {currentUserOptionLabel}
            </Button>
            <Button
                id='organization-wide-button'
                className={classnames(
                    buttonClassName,
                    selectorState === InvoicesOrganizationWideSelectorState.OrganizationWide
                        ? buttonActiveClassName
                        : buttonInactiveClassName
                )}
                aria-label={organizationWideOptionAriaLabel || organizationWideOptionLabel}
                title={organizationWideOptionAriaLabel || organizationWideOptionLabel}
                onClick={() => onClickHandler(InvoicesOrganizationWideSelectorState.OrganizationWide)}
                {...organizationWideAttributes}
            >
                {organizationWideOptionLabel}
            </Button>
            <Button
                id='show-all-button'
                className={classnames(
                    buttonClassName,
                    selectorState === InvoicesOrganizationWideSelectorState.ShowAll ? buttonActiveClassName : buttonInactiveClassName
                )}
                aria-label={showAllOptionAriaLabel || showAllOptionLabel}
                title={showAllOptionAriaLabel || showAllOptionLabel}
                onClick={() => onClickHandler(InvoicesOrganizationWideSelectorState.ShowAll)}
                {...showAllAttributes}
            >
                {showAllOptionLabel}
            </Button>
        </div>
    );
};

/**
 * Invoices organization-wide selector component.
 */
export const InvoicesOrganizationWideSelectorComponent: React.FunctionComponent<IInvoicesOrganizationWideSelectorProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IInvoicesOrganizationWideSelectorProps
>('InvoicesOrganizationWideSelectorComponent', {
    component: InvoicesOrganizationWideSelectorFunctionalComponent
});
